/**
 * Component: Product form
 * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form
 *
 * @namespace productQuickviewForm
 */
import axios from 'axios';
import dayjs from 'dayjs';
import {formatMoney} from '@shopify/theme-currency';
import Cookies from 'js-cookie';
import cssClasses from '../helpers/cssClasses';
import {on, serialize} from '../helpers/utils';
import {fetchProductInventory} from '../helpers/product-inventory';
import QuantitySelector from './quantity-selector';
import productSwatches from './product-swatches';
import productGallery from './product-gallery';

/**
 * DOM selectors.
 */
const selectors = {
  addToCartForm: '[js-product-form="addToCart"]',
  addToCart: '[js-ajax-cart="addToCart"]',
  comparePrice: '[js-product-form="comparePrice"]',
  comparePriceInput: '[js-product-form="comparePriceInput"]',
  container: '[js-product-form="container"]',
  masterSelector: '[js-product-form="masterSelector"]',
  optionSelector: '[js-product-form="optionSelector"]',
  price: '[js-product-form="price"]',
  gwprice: '[js-product-form="gw-price"]',
  priceWrapper: '[js-product-form="priceWrapper"]',
  quantitySelector: '[js-quantity-selector="container"]',
  stockMessage: '[js-product-form="inStock"]',
  preOrderDate: '[js-product-form="preOrderDate"]',
  preOrderField: '[js-product-form="preOrderField"]',
  preOrderDateField: '[js-product-form="preOrderDateField"]',
};

/**
 * Create a new product form object.
 */
export default (config) => {

  /**
   * Instance settings.
   */
  const settings = {
    enableHistoryState: config.enableHistoryState || false,
    enableSwatches: config.enableSwatches || false,
  };

  /**
   * DOM node selectors.
   */
  const container = document.querySelector(`[js-product-form="${config.container}"]`);
  const nodeSelectors = {
    addToCartForm: container.querySelector(selectors.addToCartForm),
    addToCart: container.querySelector(selectors.addToCart),
    comparePrice: container.querySelector(selectors.comparePrice),
    comparePriceInput: container.querySelector(selectors.comparePriceInput),
    masterSelector: container.querySelector(selectors.masterSelector),
    optionSelector: container.querySelectorAll(selectors.optionSelector),
    price: container.querySelector(selectors.price),
    gwprice: container.querySelector(selectors.gwprice),
    priceWrapper: container.querySelector(selectors.priceWrapper),
    quantitySelector: container.querySelector(selectors.quantitySelector),
    stockMessage: container.querySelector(selectors.stockMessage),
    preOrderDate: container.querySelector(selectors.preOrderDate),
    preOrderField: container.querySelector(selectors.preOrderField),
    preOrderDateField: container.querySelector(selectors.preOrderDateField),
  };

  /**
   * Global instances.
   */
  const product = JSON.parse(document.querySelector('[js-product-data]').innerHTML);

  let currentVariant = {};

  /**
   * Initialise component.
   */
  function init() {

    if (currentVariant) {
      currentVariant = getVariantFromOptions();
    }

    constructDefaultState();
    updateVariantOptions(product, currentVariant);
    setListeners();
    setOptionSelectorEvents();
    setAddToCartEvents();
    getLocationStock(currentVariant);
  }

  /**
   * Set change events on option selectors.
   */
  function setOptionSelectorEvents() {
    [...nodeSelectors.optionSelector].forEach((element) => {
      const type = element.getAttribute('type');

      if (type === 'radio' || type === 'checkbox') {
        on('click', element, () => onSelectChange());
        return;
      }
      on('change', element, () => onSelectChange());
    });
  }

  /**
   * Set listeners.
   */
  function setListeners() {
    Frame.EventBus.listen('Variant:optionChange', (response) => {
      updateAddToCartState(response);
      updateStockLabelState(response);
      getLocationStock(response);
    });
    Frame.EventBus.listen('Variant:priceChange', (response) => onVariantPriceChange(response));
  }

  /**
    * Set click events on buttons to post form data.
    */
  function setAddToCartEvents() {
    const element = container.querySelector(selectors.addToCart);
    on('click', element, (event) => handleAddToCart(event));
    on('keydown', element, (event) => handleAddToCart(event));
  }

  /**
   * Check if key down event.
   * @param {Event} event - Keydown event object.
   */
  function isKeyDownEvent(event) {
    return event.type === 'keydown';
  }

  /**
   * Check if the return key is pressed.
   * @param {Event} event - Key code event.
   */
  function isKeyPressIsEnter(event) {
    return event.which === 13;
  }

  /**
   * Set loading state.
   * @param {object} target clicked add to cart button in DOM.
   */
  function setLoadingState(target) {
    if (target !== nodeSelectors.addToCart) {
      return;
    }
    nodeSelectors.addToCart.innerText = theme.strings.addingToCart;
  }

  /**
  * Reset the loading states back to default.
  * @param {object} target clicked add to cart button in DOM.
  */
  function setDefaultState(target) {
    if (target !== nodeSelectors.addToCart) {
      return;
    }
    if (target.dataset.preorder === 'true') {
      nodeSelectors.addToCart.innerText = theme.strings.preorder;
      return;
    }
    nodeSelectors.addToCart.innerText = theme.strings.addToCart;
  }

  /**
   * Updates product form inputs to remove incompatible options on variant change
   * @param {object} productData - product data
   * @param {object} variant - product variant data
   */
  function updateVariantOptions(productData, variant) {
    const optionsToUpdate = getOptionsToUpdate(productData);
    const matchingVariants = filterVariantsByOption1(productData.variants, variant);

    optionsToUpdate.forEach((option) => {
      const optionValues = getUniqueOptionValues(matchingVariants, option);
      updateOptionSelect(option, optionValues, variant);
    });
  }

  /**
   * Loops through options and updates option select with new values
   * Uses currentVariant to add a selected value to the dropdown
   * @param {string} option - key of option to update e.g. 'option1'
   * @param {object} values - array of option values to generate elements
   * @param {object} currentVariant - current variant data
   */
  function updateOptionSelect(option, values, currentVariant) {
    const optionSelect = document.querySelector(`${selectors.optionSelector}[data-index=${option}]`);
    const formattedValues = values.map((value) => renderSelectOption(value, currentVariant[option])).join('');
    optionSelect.innerHTML = formattedValues;
  }

  /**
   * Outputs option HTML for injecting into select element
   * Checks currentValue to add a selected attribute to new option
   * @param {string} value - option value to be added to the select
   * @param {string} currentValue - currentValue string
   */
  function renderSelectOption(value, currentValue) {
    return `
      <option value='${value}' ${value === currentValue ? 'selected="selected"' : ''}>
        ${value}
      </option>
    `;
  }

  /**
   * Loops through variants and outputs unique values of all option types
   * @param {variants} variants - variant array
   * @param {string} option - option name
   */
  function getUniqueOptionValues(variants, option) {
    const uniqueOptionValues = getObjectPropertyValues(variants, option).filter(onlyUnique);
    return uniqueOptionValues;
  }

  function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  function getObjectPropertyValues(array, property) {
    return array.map((item) => item[property]);
  }

  function getOptionsToUpdate(productData) {

    return productData.options
      .map((item, index) => `option${index + 1}`)
      .slice(1);
  }

  function filterVariantsByOption1(variants, filterVariant) {
    return variants.filter((variant) => {
      return variant.option1 === filterVariant.option1;
    });
  }

  /**
   * Handle add to cart event and set loading state.
   * @param {object} event - Click and key down event.
   */
  function handleAddToCart(event) {
    console.log('adding to cart....', event)
    if (isKeyDownEvent(event) && !isKeyPressIsEnter(event)) {
      return;
    }

    event.preventDefault();
    const target = event.currentTarget;

    setLoadingState(target);

    axios.post(`${theme.routes.cartAddUrl}.js`, serialize(target.closest('form')))
      .then((response) => {
        Frame.EventBus.emit('AjaxCart:itemAdded', response);
        setDefaultState(target);
        return response;
      })
      .catch((error) => {
        setDefaultState();
        return error;
      });
  }

  /**
   * Get the currently selected options from product form.
   * Works with all form input elements.
   * @return {array} options - Values of currently selected variants.
   */
  function getCurrentOptions() {
    const currentOptions = [];

    [...nodeSelectors.optionSelector].forEach((element) => {
      const type = element.getAttribute('type');
      const currentOption = {};

      if (type === 'radio' || type === 'checkbox') {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;

          currentOptions.push(currentOption);
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;

        currentOptions.push(currentOption);
      }
    });

    return currentOptions;
  }

  /**
    * Find variant based on selected values.
    * @return {object || undefined} found - Variant object from product.variants.
    */
  function getVariantFromOptions() {
    const selectedValues = getCurrentOptions();
    const variants = product.variants;
    let found = false;

    variants.forEach((variant) => {
      let satisfied = true;

      selectedValues.forEach((option) => {
        if (satisfied) {
          satisfied = (option.value === variant[option.index]);
        }
      });

      if (satisfied) {
        found = variant;
      }
    });

    if (!found) {
      found = getNextValidOption(selectedValues);
    }

    return found || null;
  }

  function getNextValidOption(selectedValues) {
    const selectedOptionOne = selectedValues.find((value) => value.index === 'option1');
    const nextClosestValue = product.variants.find((variant) => variant.option1 === selectedOptionOne.value);
    return nextClosestValue;
  }

  function getLocationStock(variant) {
    if (!variant) {
      return;
    }

    const productId = product.id;
    const productHandle = product.handle;
    fetchProductInventory(productId, productHandle)
      .then((response) => {
        const productData = {
          variant,
          productStockData: response,
        };

        Frame.EventBus.emit('StockRequest', productData);
        return productData;
      })
      .catch((error) => {
        return error;
      });
  }

  /**
   * Event handler for when a variant price changes.
   */
  function onVariantPriceChange(response) {
    console.log('quickview variant price change')
    const giftPrice = nodeSelectors?.gwprice?.dataset?.gwprice || '-'
    const price = giftPrice !== '-' ? giftPrice : response.price;
    nodeSelectors.price.innerText = formatMoney(price, theme.moneyFormat);
    nodeSelectors.price.dataset.price = price;
    if (nodeSelectors.comparePrice && giftPrice === 0) {
      nodeSelectors.comparePrice.dataset.price = response.compare_at_price;
      nodeSelectors.comparePrice.innerText = response.compare_at_price > response.price ? formatMoney(response.compare_at_price, theme.moneyFormat) : '';
      nodeSelectors.comparePriceInput.value = response.compare_at_price;
    }
    if (theme.features.currencyConverter) {
      Frame.MultiCurrency.CurrencyConverter.convertAll(Cookies.get('new_currency'));
    }
  }

  window.addEventListener("blubolt:quickviewPriceCheck", (evt) => {
    const variant = getVariantFromOptions();
    updatePrice(variant);
  }, false);

  /**
   * Event handler for when a variant input changes.
   */
  function onSelectChange() {
    const variant = getVariantFromOptions();

    Frame.EventBus.emit('Variant:optionChange', variant);

    if (!variant) {
      return;
    }

    if (variant.featured_image) {
      productGallery({
        container: 'quickview-container',
      }).updateMainThumbnail(variant.featured_image.id);
    }

    updateMasterSelect(variant);
    updatePrice(variant);
    checkPreOrder(variant);
    updatePreOrderDate(variant);

    if (variant.option1 !== currentVariant.option1) {
      updateVariantOptions(product, variant);
    }

    currentVariant = variant;

    // if (settings.enableHistoryState) {
    //   updateHistoryState(variant);
    // }
  }

  /**
   * Updates the DOM state of the add to cart button.
   * @param {boolean} enabled - Decides whether cart is enabled or disabled.
   * @param {string} text - Updates the text notification content of the cart.
   */
  function updateAddToCartState(variant) {
    if (variant) {
      enableAddToCart();
      nodeSelectors.priceWrapper.classList.remove(cssClasses.hidden);
    } else {
      disableAddToCart();
      nodeSelectors.priceWrapper.classList.add(cssClasses.hidden);
      nodeSelectors.addToCartForm.innerText = theme.strings.unavailable;
      return;
    }

    if (variant.available) {
      enableAddToCart();
      nodeSelectors.addToCartForm.innerText = theme.strings.addToCart;
    } else {
      disableAddToCart();
      nodeSelectors.addToCartForm.innerText = theme.strings.soldOut;
    }
  }

  /**
   * Updates the DOM state of the in stock label.
   * @param {string} text - Updates the text stock label of the product.
   */
  function updateStockLabelState(variant) {
    if (isVariantPreorder(variant)) {
      nodeSelectors.stockMessage.innerText = theme.strings.preOrder;
      return;
    }

    if (variant.available) {
      nodeSelectors.stockMessage.innerText = theme.strings.inStock;
    } else {
      nodeSelectors.stockMessage.innerText = theme.strings.outStock;
    }
  }

  /**
   * Trigger event when variant price changes.
   * @param {object} variant - Currently selected variant.
   */
  function updatePrice(variant) {
    Frame.EventBus.emit('Variant:priceChange', variant);
  }

  /**
   * Check PreOrder status.
   * @param {object} variant - Currently selected variant.
   */
  function checkPreOrder(variant) {
    const masterSelector = nodeSelectors.masterSelector;
    const preOrderDataset = masterSelector.options[masterSelector.selectedIndex].dataset.preorder;
    const hasPreorderTag = product.tags.some((tag) => tag.indexOf('preorder') > -1);

    if (hasPreorderTag && preOrderDataset === 'true') {
      enableAddToCart();
      nodeSelectors.addToCart.innerText = theme.strings.preOrder;
      nodeSelectors.addToCart.dataset.preorder = 'true';
    } else {
      nodeSelectors.addToCart.dataset.preorder = 'false';
    }

    updatePreOrderField(variant, preOrderDataset);
  }

  function updatePreOrderField(variant, preorderDataset) {
    if (preorderDataset === 'true') {
      nodeSelectors.preOrderField.value = true;
      return;
    }
    nodeSelectors.preOrderField.value = false;
  }

  function isVariantPreorder(variant) {
    return product.tags.some((tag) => {
      return tag.indexOf('preorder') > -1 && tag.indexOf(variant.sku) > -1;
    });
  }

  /**
   * Update PreOrder date.
   * @param {object} variant - Currently selected variant.
   */
  function updatePreOrderDate(variant) {
    const tags = product.tags;
    const masterSelector = nodeSelectors.masterSelector;
    const preOrderDataset = masterSelector.options[masterSelector.selectedIndex].dataset.preorder;
    const matchTag = tags.filter((tag) => {
      return tag.includes(`preorder_${variant.sku}`);
    });

    if (matchTag.length > 0 && preOrderDataset === 'true') {
      const variantTag = matchTag[0];
      const variantTagParts = variantTag.split('_');
      const variantTagPartsSKU = variantTagParts[1];
      const variantTagPartsDate = variantTagParts[2];
      const formattedDate = dayjs(variantTagPartsDate).format('MMM D, YYYY');

      if (variant.sku === variantTagPartsSKU && variantTagPartsDate) {
        nodeSelectors.preOrderDate.innerText = `${theme.strings.preOrderDate} ${formattedDate}`;
        nodeSelectors.preOrderDateField.value = formattedDate;
      }
    } else {
      nodeSelectors.preOrderDate.innerText = '';
      nodeSelectors.preOrderDateField.value = '';
    }
  }

  /**
   * Update hidden master select of variant change.
   * @param {object} variant - Currently selected variant.
   */
  function updateMasterSelect(variant) {
    nodeSelectors.masterSelector.value = variant.id;
  }

  /**
   * Update history state for product deeplinking.
   * @param {object} variant - Currently selected variant.
   */
  function updateHistoryState(variant) {
    if (!window.history.replaceState || !variant) {
      return;
    }

    const newurl = `
      ${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}
    `;
    window.history.replaceState({path: newurl}, '', newurl);
  }

  /**
   * Construct Quantity Selector component.
   */
  function constructQuantitySelectors() {
    new QuantitySelector(nodeSelectors.quantitySelector.id).init();
  }

  /**
   * Construct product swatches.
   */
  function constructProductSwatches() {
    if (!settings.enableSwatches) {
      return;
    }

    productSwatches().init();
  }

  /**
   * Add disable state to add to cart button.
   */
  function disableAddToCart() {
    nodeSelectors.addToCart.setAttribute('disabled', true);
    nodeSelectors.addToCart.classList.add(cssClasses.disabled);
  }

  /**
   * Set add to cart button to active.
   */
  function enableAddToCart() {
    nodeSelectors.addToCartForm.removeAttribute('disabled');
    nodeSelectors.addToCartForm.classList.remove(cssClasses.disabled);
  }

  /**
   * Construct default state.
   */
  function constructDefaultState() {
    constructProductSwatches();
    constructQuantitySelectors();
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    getVariantFromOptions,
  });
};
